(() => {
  const selectors = [
    '.product-card__content a.contents.user-select-text p',
    '.ib-product-card__info h3'
  ];
  const trackClass = 'ib-title-marquee-track';
  const marqueeClass = 'ib-title-marquee';
  const viewportClass = 'ib-title-marquee-viewport';
  const readyClass = 'ib-title-marquee-ready';
  const inViewClass = 'ib-title-marquee-in-view';
  const delayMs = 1500;
  const observed = new WeakSet();
  const timers = new WeakMap();
  let scanQueued = false;

  const viewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const title = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            title.classList.add(inViewClass);
            if (title.classList.contains(readyClass)) return;

            if (!timers.has(title)) {
              const timer = window.setTimeout(() => {
                timers.delete(title);
                if (!title.isConnected || !title.classList.contains(inViewClass)) return;
                measureTitle(title);
                if (title.classList.contains(marqueeClass)) {
                  title.classList.add(readyClass);
                }
              }, delayMs);
              timers.set(title, timer);
            }
          } else {
            title.classList.remove(inViewClass);
            const timer = timers.get(title);
            if (timer) window.clearTimeout(timer);
            timers.delete(title);
          }
        });
      }, { threshold: [0, 0.2] })
    : null;

  function getTrack(title) {
    let track = Array.from(title.children).find((child) => child.classList.contains(trackClass));
    if (!track) {
      track = document.createElement('span');
      track.className = trackClass;
      while (title.firstChild) track.appendChild(title.firstChild);
      title.appendChild(track);
    }
    return track;
  }

  function measureTitle(title) {
    const track = getTrack(title);
    const viewport = title.closest('a.contents.user-select-text') || title;
    const text = track.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;

    if (viewport !== title) viewport.classList.add(viewportClass);
    if (viewport !== title && !viewport.hasAttribute('title')) viewport.setAttribute('title', text);
    title.setAttribute('aria-label', text);

    // Enable max-content measurement, then check whether this title actually overflows.
    title.classList.add(marqueeClass);
    const availableWidth = title.clientWidth;
    const contentWidth = Math.ceil(track.getBoundingClientRect().width);
    const distance = Math.max(0, contentWidth - availableWidth);

    if (availableWidth > 0 && distance > 6 && viewObserver) {
      title.style.setProperty('--ib-title-marquee-distance', distance + 'px');
      // Slow enough to read, with a pause at both ends of the title.
      title.style.setProperty('--ib-title-marquee-duration', Math.max(5.5, Math.min(18, distance / 24 + 4)) + 's');
      if (!observed.has(title)) {
        observed.add(title);
        viewObserver.observe(title);
      }
    } else {
      title.classList.remove(marqueeClass, readyClass, inViewClass);
      title.style.removeProperty('--ib-title-marquee-distance');
      title.style.removeProperty('--ib-title-marquee-duration');
      if (observed.has(title) && viewObserver) viewObserver.unobserve(title);
      observed.delete(title);
      const timer = timers.get(title);
      if (timer) window.clearTimeout(timer);
      timers.delete(title);
    }
  }

  function scanTitles() {
    scanQueued = false;
    document.querySelectorAll(selectors.join(',')).forEach(measureTitle);
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(scanTitles);
  }

  function init() {
    scanTitles();
    window.addEventListener('resize', scheduleScan, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleScan);

    // Recommendations and other card grids can be added dynamically after page load.
    const mutationObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) scheduleScan();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();